import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ZendeskFacade } from '@core/sso/facades/zendesk.facade';
import { NotificationsFacade } from '@layout/notifications/facades/notifications.facade';
import { NotificationSource } from '@layout/notifications/models/notification-source.enum';
import { Notification } from '@layout/notifications/models/notification.interface';
import { NotificationTemplateService } from '@layout/notifications/services/notification-template.service';
import { Observable } from 'rxjs';
import { TooltipDirective } from '@kaseya-design-components';
import { TranslatePipe } from '@ngx-translate/core';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@kaseya-shared';
import { formatDate } from '@kaseya-utils';

@Component({
  selector: 'itc-notification',
  templateUrl: 'notification.component.html',
  styleUrl: 'notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgTemplateOutlet, TooltipDirective, TranslatePipe],
  host: {
    '[class.border]': 'showBorder()',
  },
})
export class NotificationComponent {
  private readonly notificationsFacade = inject(NotificationsFacade);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zendeskFacade = inject(ZendeskFacade);
  private readonly notificationTemplateService = inject(NotificationTemplateService);
  private readonly permissionsFacade = inject(PermissionsFacade);

  readonly notification = input.required<Notification>();
  readonly showBorder = input(true);

  readonly hasAccessToNotificationsModify = this.permissionsFacade.hasPermission(Permission.NotificationsModify);
  readonly formatDate = formatDate;
  readonly toggleViewabilityLabelKey = computed(() =>
    this.notification().viewed ? 'notifications.mark-as-unread' : 'notifications.mark-as-read'
  );
  readonly zendeskTicketId = computed(() => {
    const { source, zendeskTicketWasChanged, zendeskTicketFieldWasChanged } = this.notification();

    return source === NotificationSource.Zendesk
      ? (zendeskTicketWasChanged?.ticketId ?? zendeskTicketFieldWasChanged?.ticketId)
      : undefined;
  });

  toggleNotificationViewability(): void {
    if (!this.hasAccessToNotificationsModify()) {
      return;
    }

    const { id, source, viewed } = this.notification();

    this.notificationsFacade.toggleNotificationViewability(id, source, !viewed);
  }

  deleteNotification(): void {
    if (!this.hasAccessToNotificationsModify()) {
      return;
    }

    const { id, source } = this.notification();

    this.notificationsFacade.deleteNotification(id, source);
  }

  openNotification(): void {
    const ticketId = this.zendeskTicketId();

    if (!ticketId) {
      return;
    }

    this.notificationsFacade.loadZendeskSsoRequestInfo(ticketId);
    this.notificationsFacade.onLoadZendeskSsoRequestInfoSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(zendeskSsoRequestInfo => {
        this.zendeskFacade.goToZendesk(zendeskSsoRequestInfo);
      });
  }

  get notificationTitle$(): Observable<string> {
    return this.notificationTemplateService.getNotificationTitle(this.notification());
  }

  get notificationContent$(): Observable<string> {
    return this.notificationTemplateService.getNotificationContent(this.notification());
  }
}
